/**
 * 작성자 : 홍선영
 * 작성일자 : 2024.04.30
 * 작성내용 : 비디오파일 선택/재생/삭제 컴포넌트
 */

import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useRef, useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

const Root = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  .title {
    min-width: 6rem;
    font-size: 0.875rem;
    color: var(--text-primary);
  }
  .fileBoxInput {
    font-size: 0.875rem;
  }
  .play,
  .add {
    user-select: none;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    width: 3rem;
    border-radius: 0.25rem;
    .material-symbols-rounded {
      font-size: 1.5rem;
      font-variation-settings: 'FILL' 1;
    }
  }
  .play {
    background-color: var(--tonal-blue);
    .material-symbols-rounded {
      color: var(--primary);
    }
    &:hover {
      background-color: var(--tonal-deep-blue);
      &:active {
        outline: 0.125rem solid var(--primary);
        outline: 0.125rem solid var(--filled-slate);
      }
    }
  }
  .add {
    background-color: var(--tonal);
    .material-symbols-rounded {
      color: var(--filled-slate-deep);
    }
    &:hover {
      background-color: var(--tonal-deep);
      background-color: var(--filled-slate-deep);
      .material-symbols-rounded {
        color: var(--bw-inverse);
      }
      &:active {
        background-color: var(--filled-slate);
      }
    }
  }
  .delete {
    user-select: none;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    width: 3rem;
    border-radius: 0.25rem;
    background-color: var(--tonal-red);
    .material-symbols-rounded {
      font-size: 1.5rem;
      font-variation-settings: 'FILL' 1;
    }
    &:hover {
      background-color: var(--tonal-deep-red);
      &:active {
        outline: 0.125rem solid var(--filled-red);
      }
    }
  }
  .fileBoxInput {
    flex-grow: 1;
    background-color: var(--tonal-light);
    &::placeholder {
      color: var(--text-tertiary);
    }
  }
`;

interface IVideoSelect {
  caption: string;
  vType: string;
  handleFileInput: (event: any, type: string) => void;
  file: File | null;
  filePath: string;
  fileName: string;
  isEnrolled: boolean;
  onClickDeleteVideo: (vType: string) => void;
}

const VideoSelect = ({ caption, vType, handleFileInput, file, fileName, filePath, isEnrolled, onClickDeleteVideo }: IVideoSelect) => {
  const { t } = useTranslation();
  const inputFileRef = useRef<HTMLInputElement | null>(null);
  const [selectedVideoType, setSelectedVideoType] = useState<string | null>(null);

  // Flutter 환경인지 확인
  const isFlutter = () => {
    return window.VideoFilePicker !== undefined;
  };

  const onClickPlay = (enrolled: boolean, path: string, fileParam: File | null, vTypeParam: string) => {
    if (enrolled) {
      if (fileParam) {
        // 파일객체가 있을때 (신규 등록된 파일 url)
        const videoUrl = URL.createObjectURL(fileParam);
        window.open(videoUrl, '_blank', 'width=1200, height=600');
      } else {
        // URL이 있을때 (기존 비디오 저장경로 url)
        // const awsPath = 'https://itlogs3server.s3.ap-northeast-2.amazonaws.com/SafeEdu';
        // const extractedString = path.slice(awsPath.length);
        // const url = `/safeedu_video?path=${extractedString}`;
        window.open(path, '_blank', 'width=1200, height=600');
      }
    } else if (isFlutter()) {
      // Flutter 환경에서는 VideoFilePicker 사용
      window.VideoFilePicker?.postMessage('selectVideo');
      setSelectedVideoType(vTypeParam);
    } else {
      // 웹 환경에서는 기존 input 사용
      inputFileRef.current?.click();
      setSelectedVideoType(vTypeParam);
    }
  };

  // 파일 크기 체크 및 상태 업데이트 함수
  const handleFileInputChange = useCallback(
    (selectedFile: File) => {
      if (!selectedFile) {
        console.error('파일이 선택되지 않았습니다.');
        return;
      }

      const fileSize = selectedFile.size;
      // 80MB in Bytes (80 * 1024 * 1024)
      if (fileSize > 83886080) {
        toast.warning(`${t('파일크기는 80MB를 초과할 수 없습니다')} (${Math.ceil(fileSize / 1024 / 1024)} / 80MB)`);
      } else {
        selectedVideoType && handleFileInput({ target: { files: [selectedFile], value: '' } }, selectedVideoType);
      }
    },
    [handleFileInput, selectedVideoType]
  );

  // 웹 환경의 파일 선택 처리
  const handleWebFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const fileList = event.target.files;
      if (fileList && fileList.length > 0) {
        handleFileInputChange(fileList[0]);
      }
      // input 초기화
      event.target.value = '';
    },
    [handleFileInputChange]
  );

  // Flutter에서 파일 선택 핸들러 설정
  useEffect(() => {
    if (isFlutter()) {
      window.handleVideoSelected = async (videoFile: any) => {
        try {
          if (!videoFile) {
            throw new Error('비디오 파일 정보가 없습니다.');
          }

          // Data URL에서 실제 base64 데이터 추출
          const base64Data = videoFile.base64.split(',')[1];
          if (!base64Data) {
            throw new Error('base64 데이터 형식이 올바르지 않습니다.');
          }

          // base64 데이터를 Uint8Array로 변환
          const binaryString = window.atob(base64Data);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i += 1) {
            bytes[i] = binaryString.charCodeAt(i);
          }

          // File 객체 생성
          const selectedFile = new File([bytes], videoFile.name, { type: videoFile.mimeType });

          if (!selectedFile) {
            throw new Error('파일 객체 생성에 실패했습니다.');
          }

          handleFileInputChange(selectedFile);
        } catch (error) {
          console.error('비디오 파일 처리 중 오류:', error);
          toast.error(t('비디오 파일 처리 중 오류가 발생했습니다'));
        }
      };

      window.handleVideoError = (error: string) => {
        console.error('비디오 선택 오류:', error);
        toast.error(t('비디오 선택 중 오류가 발생했습니다'));
      };
    }
  }, [handleFileInputChange]);

  return (
    <Root>
      <label
        htmlFor='tsTitle' //
        className={`title ${vType === 'ko' ? 'required' : ''}`}
      >
        {t(caption)}
      </label>
      <label
        htmlFor='filebox1' //
        className={`searchBox flex-center ${isEnrolled ? 'play' : 'add'}`}
        role='presentation'
        onClick={() => onClickPlay(isEnrolled, filePath, file, vType)}
      >
        <span className='material-symbols-rounded'>{isEnrolled ? 'play_arrow' : 'add'}</span>
      </label>

      {!isEnrolled && !isFlutter() && (
        <input
          className='hiddenFileBox' //
          type='file'
          accept='video/mp4'
          id='filebox'
          name='dIfilename'
          ref={inputFileRef}
          onChange={handleWebFileSelect}
        />
      )}
      <input
        placeholder={t('80MB 까지 첨부 가능')} //
        className={`fileBoxInput ${isEnrolled ? '' : 'guide'}`}
        style={{ width: '50%', lineHeight: '2' }}
        type='text'
        id='filebox'
        name='dIfilename'
        value={fileName || ''}
        disabled
      />
      {fileName && (
        <label
          htmlFor='del' //
          className={`delete red ${isEnrolled ? '' : 'disabled'}`}
          role='presentation'
          onClick={() => onClickDeleteVideo(vType)}
        >
          <span className='material-symbols-rounded'>delete</span>
        </label>
      )}
    </Root>
  );
};

export default VideoSelect;
